import {
    MathUtils
} from "src/app/shared/utils";

interface ParsedBarcode {
    raw: string;
    W05: number;   // Weight (e.g., 00120 → 0.120 kg)
    P06: number;   // Unit amount
    Z06: number;   // Price (e.g., 000800 → 8.00€)
    F06: number;   // Total amount
    H06: string;   // PLU barcode
    u02?: 0 | 1;   // Line type (0=weight, 1=by piece)
    Q02?: number;  // Quantity
}

type ParsedItem = {
    barcode: string;
    price: number;
    quantity: number;
    plu: string;
}

export class HelmacBarcode {
    private static tryParse(s: string): ParsedBarcode | null {
        if (!/^\d+$/.test(s)) {
            return null;
        }

        const Z06 = parseInt(s.slice(0, 6));
        const W05 = parseInt(s.slice(6, 11));
        const P06 = parseInt(s.slice(11, 17));
        const F06 = parseInt(s.slice(17, 23));
        const H06 = s.slice(23, 29);

        const base = {
            Z06,
            W05,
            P06,
            F06,
            H06
        };

        if (s.length === 29) {
            return {
                ...base,
                raw: s,
            };
        }

        if (s.length === 31) {
            const suffix = parseInt(s.slice(29, 31));

            // u02: can only be 0 or 1
            if (suffix === 0 || suffix === 1) {
                // Disambiguation: if W05 > 0 it is by weight → u02=0; if W05 = 0 it is by piece → u02=1
                const expectedU02 = W05 > 0
                    ? 0
                    : 1;

                if (suffix === expectedU02) {
                    return {
                        ...base,
                        raw: s,
                        u02: suffix
                    };
                }
            }

            // Q02: quantity 1-99
            if (suffix >= 1 && suffix <= 99) {
                return {
                    ...base,
                    raw: s,
                    Q02: suffix
                };
            }
        }

        return null;
    }

    public static parseBarcode(raw: string, length: number = 29): ParsedItem | null {
        if (raw.length < length) {
            return null;
        }

        const parsed = HelmacBarcode.tryParse(raw.slice(0, length));

        if (parsed) {
            const hasWeight = parsed.W05 !== 0;

            const quantity = hasWeight
                ? MathUtils.round(parsed.W05 / 1000, 3)
                : parsed.Q02 || 1;

            return {
                barcode: parsed.raw,
                price: MathUtils.round(parsed.Z06 / 100, 2),
                plu: parsed.H06,
                quantity: quantity
            };
        }

        return null;
    }

    public static parseMultipleBarcodes(input: string, length: number = 29): ParsedItem[] {
        const results: ParsedItem[] = [];
        let pos = 0;

        while (pos < input.length) {
            const parsed = HelmacBarcode.parseBarcode(input.slice(pos, pos + length), length);

            if (parsed) {
                results.push(parsed);
            }

            pos += length;
        }

        return results;
    }
}
